import {useDraggable} from "@dnd-kit/core";
import {MoneyLabel} from "../../../../../../components/general/MoneyLabel.tsx";
import {XMarkIcon, DocumentIcon, DocumentTextIcon, ReceiptPercentIcon} from "@heroicons/react/24/outline";
import {AttachedItem, DEFAULT_CURRENCY} from "../dndTypes.ts";
import {formatDate} from "../utils/utils.ts";
import {cleanDocumentFileName} from "../../../../../../lib/TFICLib.ts";
import {DescriptionWithTooltip} from "./DescriptionWithTooltip.tsx";

interface DraggableAttachedItemProps {
    item: AttachedItem;
    currency: string;
    onRemove: () => void;
    isDragging: boolean;
    onOpenDocument?: (docId: string) => void;
}

export function DraggableAttachedItem({
    item,
    currency,
    onRemove,
    isDragging,
    onOpenDocument,
}: DraggableAttachedItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
    } = useDraggable({id: item.id});

    const style = {
        opacity: isDragging ? 0.5 : 1,
    };

    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onRemove();
    };

    const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
        e.stopPropagation();
    };

    const getDocumentIcon = (category?: string) => {
        if (!category) return null;
        const categoryLower = category.toLowerCase();
        if (categoryLower === 'invoice') {
            return DocumentTextIcon;
        } else if (categoryLower === 'pos_receipt') {
            return ReceiptPercentIcon;
        } else {
            return DocumentIcon;
        }
    };

    const IconComponent = getDocumentIcon(item.document_category);

    const getDisplayParts = () => {
        if (!item.metadata) return {reference: null, filename: null};
        if (item.document_category) {
            const parts = item.metadata.split(" ");
            const reference = parts.length ? parts[0] : null;
            const filename = parts.length > 1 ? parts.slice(1).join(" ") : null;
            return {
                reference,
                filename: filename ? cleanDocumentFileName(filename) : null,
            };
        }
        return {reference: null, filename: item.metadata};
    };

    const {reference, filename} = getDisplayParts();

    const openDocument = item.document_category ? () => onOpenDocument?.(item.id) : undefined;

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`grid grid-cols-[48px,1fr,auto,auto] items-center gap-3 border border-slate-200 rounded-md px-4 py-3 bg-white cursor-grab active:cursor-grabbing hover:shadow-sm transition relative z-0 hover:z-10 ${
                isDragging ? "opacity-50" : ""
            }`}
        >
            <div className="flex items-center justify-center">
                {IconComponent && (
                    <div className="h-10 w-10 rounded-full flex-shrink-0 flex items-center justify-center bg-gray-100">
                        <IconComponent className="h-6 w-6 text-gray-600" aria-hidden="true"/>
                    </div>
                )}
            </div>

            <div className="flex flex-col min-w-0 text-left">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                        {item.relation_name}
                    </div>
                    {item.date && (
                        <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDate(item.date)}
                        </span>
                    )}
                </div>
                {filename && (
                    <DescriptionWithTooltip
                        description={filename}
                        onClick={openDocument}
                        containerClassName="min-w-0"
                        clampClassName="truncate"
                        textClassName="text-gray-700"
                    />
                )}
            </div>

            <div className="flex items-center justify-end">
                {reference && (
                    <span className="text-xs text-gray-600 border border-gray-300 rounded-md px-2 py-0.5 bg-gray-50 text-center">
                        {reference}
                    </span>
                )}
            </div>

            <div className="flex items-center justify-end gap-2">
                {item.document_direction ? (
                    <span className={item.document_direction === 'purchase_invoice' ? 'text-red-600' : 'text-green-600'}>
                        <MoneyLabel
                            value={item.amount.signed_amount / 100}
                            colorAid={false}
                            currency={item.amount.currency || currency || DEFAULT_CURRENCY}
                            showCurrency={true}
                        />
                    </span>
                ) : (
                    <MoneyLabel
                        value={item.amount.signed_amount / 100}
                        colorAid={true}
                        currency={item.amount.currency || currency || DEFAULT_CURRENCY}
                        showCurrency={true}
                    />
                )}
                <button
                    type="button"
                    className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-colors"
                    onClick={handleRemove}
                    onPointerDown={stopPropagation}
                    onMouseDown={stopPropagation}
                    onMouseUp={stopPropagation}
                    aria-label="Remove attachment"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
